/* AgentMeeting — i18n (zh / en) */
import React from 'react';

const LANG_KEY = 'agentmeeting.lang';

export type Lang = 'zh' | 'en';

export const I18N_DICT: Record<Lang, Record<string, string>> = {
  zh: {
    'nav.features': '功能',
    'nav.howItWorks': '工作方式',
    'nav.pricing': '定价',
    'nav.useCases': '场景',
    'nav.docs': '文档',
    'nav.changelog': '更新日志',
    'cta.signin': '登录',
    'cta.startFree': '免费开始 →',
    'cta.openApp': '打开应用 →',

    'land.eyebrow': 'v0.4.2 · 公开测试 · 邀请制',
    'land.h1.line1': '与 AI 一起开会探讨。',
    'land.h1.line2': '每一个 AI, 都能给你不一样的视角。',
    'land.lead': 'AgentMeeting 把多个 LLM agent 放在同一张圆桌上 — 辩论、决策、红蓝对抗、苏格拉底式诘问。你可以做参会者、主持人, 或导演视角全程操盘。每一句发言都共享上下文、可回放、可分叉、按 token 计价。',
    'land.cta.primary': '$ 开 5 分钟 demo 会议',
    'land.cta.secondary': '看 60 秒 demo 视频',
    'land.cta.note': '注册即送 $5 LLM credits · 不要信用卡 · 邀请制',

    'sec.features': '功能',
    'sec.howItWorks': '工作方式',
    'sec.useCases': '场景',
    'sec.faq': '常见问题',

    'final.h2.line1': '你下一个艰难的决定,',
    'final.h2.line2': '让六个 AI 替你吵一吵。',
    'final.sub': '注册 → 选模板 → 给个议题 → 5 分钟内出结论。',
    'final.cta': '$ 立即开始',

    'foot.product': '产品',
    'foot.company': '公司',
    'foot.resources': '资源',
    'foot.legal': '法务',
    'foot.copy': '© 2026 AgentMeeting Labs · 保留所有权利',

    'pricing.h1': '透明定价 · 按 token 计价, 0 加价',
    'pricing.lead': '订阅含基础时长 + LLM credits, 超额按真实成本走。我们不在 token 上加价。',

    'auth.signup': '免费注册',
    'auth.signin': '登录',
    'auth.signup.lead': '免费, 不要信用卡。注册即送 $5 LLM credits。',
    'auth.signin.lead': '把 6 个 AI 叫到你这边继续吵。',

    'docs.h1': '文档',
    'docs.lead': '从 0 到第一场决策会议, 大约 5 分钟。',

    'log.h1': '更新日志',
    'log.lead': '每周发版, 不每周加冗余。',

    /* ===== App pages ===== */
    /* topbar */
    'app.topbar.tab.agenda': '议程',
    'app.topbar.tab.room': '会议室',
    'app.topbar.tab.resolution': '决议',
    'app.topbar.tab.replay': '回放',
    'app.topbar.tab.studio': '工作室',
    'app.topbar.tab.meetings': '我的会议',
    'app.topbar.live': '直播中',
    'app.topbar.role.participant': '参会者',
    'app.topbar.role.host': '主持人',
    'app.topbar.role.director': '导演',
    'app.topbar.role.observer': '旁听',
    'app.topbar.role.switcher': '切换角色',

    /* agenda */
    'app.agenda.title': '会议标题',
    'app.agenda.title.placeholder': '给这场会议起个名字',
    'app.agenda.type': '会议类型',
    'app.agenda.type.debate': '辩论',
    'app.agenda.type.decision': '决策',
    'app.agenda.type.redblue': '红蓝对抗',
    'app.agenda.type.socratic': '苏格拉底诘问',
    'app.agenda.motion': '议题/动议',
    'app.agenda.motion.placeholder': '我们要决定什么?',
    'app.agenda.participants': '参会者',
    'app.agenda.participants.add': '添加 Agent',
    'app.agenda.budget': '预算 (token)',
    'app.agenda.budget.hint': '超出后会议自动结束',
    'app.agenda.rounds': '回合数',
    'app.agenda.rounds.hint': '每个 agent 在每回合发言一次',
    'app.agenda.rules': '调度规则',
    'app.agenda.rules.roundRobin': '轮流发言',
    'app.agenda.rules.moderator': '主持人点名',
    'app.agenda.rules.free': '自由抢麦',
    'app.agenda.instruction': '系统指令',
    'app.agenda.instruction.placeholder': '给所有 agent 的总规则',
    'app.agenda.termination': '终止条件',
    'app.agenda.termination.consensus': '达成共识',
    'app.agenda.termination.budget': '预算耗尽',
    'app.agenda.termination.rounds': '回合数到达',
    'app.agenda.termination.manual': '手动停止',
    'app.agenda.timeline': '时间轴预览',
    'app.agenda.addRound': '加一回合',
    'app.agenda.save': '保存议程',
    'app.agenda.start': '开始会议',

    /* studio */
    'app.studio.persona': '人设',
    'app.studio.persona.placeholder': '这位 agent 是谁?',
    'app.studio.stance': '立场',
    'app.studio.stance.pro': '赞成',
    'app.studio.stance.con': '反对',
    'app.studio.stance.neutral': '中立',
    'app.studio.model': '模型',
    'app.studio.temperature': '温度',
    'app.studio.maxTokens': '最大 token',
    'app.studio.knowledge': '知识库',
    'app.studio.knowledge.upload': '上传文档',
    'app.studio.tools': '工具',
    'app.studio.tools.search': '联网搜索',
    'app.studio.tools.code': '代码执行',
    'app.studio.tools.calc': '计算器',
    'app.studio.dryRun': '试运行',
    'app.studio.save': '保存 Agent',
    'app.studio.duplicate': '复制',

    /* room */
    'app.room.participants': '参会者',
    'app.room.agenda': '议程',
    'app.room.requestMic': '请求发言',
    'app.room.send': '发送',
    'app.room.send.placeholder': '说点什么...',
    'app.room.advanceRound': '进入下一回合',
    'app.room.callOn': '点名发言',
    'app.room.openVote': '发起投票',
    'app.room.director.controls': '导演控制台',
    'app.room.director.pause': '暂停',
    'app.room.director.resume': '继续',
    'app.room.director.rewind': '回退',
    'app.room.director.fork': '分叉',

    /* replay */
    'app.replay.branch': '分支',
    'app.replay.fork': '分叉',
    'app.replay.rewindHere': '回退到此',
    'app.replay.forkHere': '从此分叉',
    'app.replay.branchGraph': '分支图',
    'app.replay.cursor.inspect': '游标查看',
    'app.replay.mutations': '变更记录',
    'app.replay.timeline': '时间轴',

    /* resolution */
    'app.resolution.vote': '投票',
    'app.resolution.ballot': '选票',
    'app.resolution.tally': '计票',
    'app.resolution.consensus': '共识检测',
    'app.resolution.stanceDrift': '立场漂移',
    'app.resolution.openQuestions': '遗留问题',
    'app.resolution.minutes': '会议纪要',
    'app.resolution.export': '导出',

    /* meetings */
    'app.meetings.my': '我的会议',
    'app.meetings.motion': '议题',
    'app.meetings.status': '状态',
    'app.meetings.status.live': '进行中',
    'app.meetings.status.done': '已结束',
    'app.meetings.status.draft': '草稿',
    'app.meetings.duration': '时长',
    'app.meetings.cost': '花费',
    'app.meetings.new': '新建会议',
    'app.meetings.search': '搜索会议',
  },
  en: {
    'nav.features': 'Features',
    'nav.howItWorks': 'How it works',
    'nav.pricing': 'Pricing',
    'nav.useCases': 'Use cases',
    'nav.docs': 'Docs',
    'nav.changelog': 'Changelog',
    'cta.signin': 'Sign in',
    'cta.startFree': 'Start free →',
    'cta.openApp': 'Open app →',

    'land.eyebrow': 'v0.4.2 · public beta · invite only',
    'land.h1.line1': 'Hold a meeting with AIs.',
    'land.h1.line2': 'Every AI gives you a different perspective.',
    'land.lead': 'AgentMeeting puts multiple LLM agents at the same round table — debate, decision, red-team vs blue-team, Socratic interrogation. You can be a participant, the host, or a director driving the whole thing. Every utterance shares context, can be replayed, forked, and billed by token.',
    'land.cta.primary': '$ Run a 5-min demo meeting',
    'land.cta.secondary': 'Watch 60-sec demo',
    'land.cta.note': 'Get $5 in LLM credits · no card · invite only',

    'sec.features': 'Features',
    'sec.howItWorks': 'How it works',
    'sec.useCases': 'Use cases',
    'sec.faq': 'FAQ',

    'final.h2.line1': 'Your next hard call —',
    'final.h2.line2': 'let six AIs argue it out.',
    'final.sub': 'Sign up → pick a pack → drop a topic → ship a verdict in 5 min.',
    'final.cta': '$ Start now',

    'foot.product': 'Product',
    'foot.company': 'Company',
    'foot.resources': 'Resources',
    'foot.legal': 'Legal',
    'foot.copy': '© 2026 AgentMeeting Labs · all rights reserved',

    'pricing.h1': 'Transparent pricing · billed by token, 0 markup',
    'pricing.lead': 'Subscriptions include included minutes + LLM credits. Overage runs at true cost. We do not mark up tokens.',

    'auth.signup': 'Sign up',
    'auth.signin': 'Sign in',
    'auth.signup.lead': 'Free, no card. $5 in LLM credits on signup.',
    'auth.signin.lead': 'Bring your six AIs back to the table.',

    'docs.h1': 'Documentation',
    'docs.lead': 'From zero to your first decision meeting in about 5 min.',

    'log.h1': 'Changelog',
    'log.lead': 'Weekly releases, no weekly bloat.',

    /* ===== App pages ===== */
    /* topbar */
    'app.topbar.tab.agenda': 'Agenda',
    'app.topbar.tab.room': 'Room',
    'app.topbar.tab.resolution': 'Resolution',
    'app.topbar.tab.replay': 'Replay',
    'app.topbar.tab.studio': 'Studio',
    'app.topbar.tab.meetings': 'Meetings',
    'app.topbar.live': 'LIVE',
    'app.topbar.role.participant': 'Participant',
    'app.topbar.role.host': 'Host',
    'app.topbar.role.director': 'Director',
    'app.topbar.role.observer': 'Observer',
    'app.topbar.role.switcher': 'Switch role',

    /* agenda */
    'app.agenda.title': 'Meeting title',
    'app.agenda.title.placeholder': 'Name this meeting',
    'app.agenda.type': 'Meeting type',
    'app.agenda.type.debate': 'Debate',
    'app.agenda.type.decision': 'Decision',
    'app.agenda.type.redblue': 'Red vs Blue',
    'app.agenda.type.socratic': 'Socratic',
    'app.agenda.motion': 'Motion',
    'app.agenda.motion.placeholder': 'What are we deciding?',
    'app.agenda.participants': 'Participants',
    'app.agenda.participants.add': 'Add agent',
    'app.agenda.budget': 'Budget (tokens)',
    'app.agenda.budget.hint': 'Meeting auto-ends past this',
    'app.agenda.rounds': 'Rounds',
    'app.agenda.rounds.hint': 'Each agent speaks once per round',
    'app.agenda.rules': 'Scheduling rules',
    'app.agenda.rules.roundRobin': 'Round robin',
    'app.agenda.rules.moderator': 'Moderator picks',
    'app.agenda.rules.free': 'Free for all',
    'app.agenda.instruction': 'System instruction',
    'app.agenda.instruction.placeholder': 'Global rules for every agent',
    'app.agenda.termination': 'Termination',
    'app.agenda.termination.consensus': 'On consensus',
    'app.agenda.termination.budget': 'Budget exhausted',
    'app.agenda.termination.rounds': 'Rounds reached',
    'app.agenda.termination.manual': 'Manual stop',
    'app.agenda.timeline': 'Timeline preview',
    'app.agenda.addRound': 'Add round',
    'app.agenda.save': 'Save agenda',
    'app.agenda.start': 'Start meeting',

    /* studio */
    'app.studio.persona': 'Persona',
    'app.studio.persona.placeholder': 'Who is this agent?',
    'app.studio.stance': 'Stance',
    'app.studio.stance.pro': 'Pro',
    'app.studio.stance.con': 'Con',
    'app.studio.stance.neutral': 'Neutral',
    'app.studio.model': 'Model',
    'app.studio.temperature': 'Temperature',
    'app.studio.maxTokens': 'Max tokens',
    'app.studio.knowledge': 'Knowledge',
    'app.studio.knowledge.upload': 'Upload doc',
    'app.studio.tools': 'Tools',
    'app.studio.tools.search': 'Web search',
    'app.studio.tools.code': 'Code exec',
    'app.studio.tools.calc': 'Calculator',
    'app.studio.dryRun': 'Dry run',
    'app.studio.save': 'Save agent',
    'app.studio.duplicate': 'Duplicate',

    /* room */
    'app.room.participants': 'Participants',
    'app.room.agenda': 'Agenda',
    'app.room.requestMic': 'Request mic',
    'app.room.send': 'Send',
    'app.room.send.placeholder': 'Say something...',
    'app.room.advanceRound': 'Advance round',
    'app.room.callOn': 'Call on',
    'app.room.openVote': 'Open vote',
    'app.room.director.controls': 'Director controls',
    'app.room.director.pause': 'Pause',
    'app.room.director.resume': 'Resume',
    'app.room.director.rewind': 'Rewind',
    'app.room.director.fork': 'Fork',

    /* replay */
    'app.replay.branch': 'Branch',
    'app.replay.fork': 'Fork',
    'app.replay.rewindHere': 'Rewind to here',
    'app.replay.forkHere': 'Fork from here',
    'app.replay.branchGraph': 'Branch graph',
    'app.replay.cursor.inspect': 'Cursor inspect',
    'app.replay.mutations': 'Mutations',
    'app.replay.timeline': 'Timeline',

    /* resolution */
    'app.resolution.vote': 'Vote',
    'app.resolution.ballot': 'Ballot',
    'app.resolution.tally': 'Tally',
    'app.resolution.consensus': 'Consensus detector',
    'app.resolution.stanceDrift': 'Stance drift',
    'app.resolution.openQuestions': 'Open questions',
    'app.resolution.minutes': 'Minutes',
    'app.resolution.export': 'Export',

    /* meetings */
    'app.meetings.my': 'My meetings',
    'app.meetings.motion': 'Motion',
    'app.meetings.status': 'Status',
    'app.meetings.status.live': 'Live',
    'app.meetings.status.done': 'Done',
    'app.meetings.status.draft': 'Draft',
    'app.meetings.duration': 'Duration',
    'app.meetings.cost': 'Cost',
    'app.meetings.new': 'New meeting',
    'app.meetings.search': 'Search meetings',
  },
};

interface I18nCtxValue {
  lang: Lang;
  t: (key: string, fallback?: string) => string;
  setLang: (next: Lang) => void;
}

const I18nCtx = React.createContext<I18nCtxValue>({
  lang: 'zh',
  t: (k) => k,
  setLang: () => {},
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangRaw] = React.useState<Lang>(() => {
    try {
      const v = localStorage.getItem(LANG_KEY);
      return (v === 'en' || v === 'zh') ? v : 'zh';
    } catch { return 'zh'; }
  });
  const setLang = React.useCallback((next: Lang) => {
    setLangRaw(next);
    try { localStorage.setItem(LANG_KEY, next); } catch { /* ignore */ }
    document.documentElement.lang = next === 'zh' ? 'zh-CN' : 'en';
  }, []);
  const t = React.useCallback((key: string, fallback?: string) => {
    const dict = I18N_DICT[lang] || I18N_DICT.zh;
    return dict[key] != null ? dict[key] : (fallback != null ? fallback : key);
  }, [lang]);
  React.useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  }, [lang]);
  return <I18nCtx.Provider value={{ lang, t, setLang }}>{children}</I18nCtx.Provider>;
}

export function useI18n() { return React.useContext(I18nCtx); }

export function LangSwitch() {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex" style={{
      border: '1px solid var(--line-2)', borderRadius: 100, padding: 2,
      fontFamily: 'var(--mono)', fontSize: 10,
    }}>
      {(['zh', 'en'] as Lang[]).map(l => (
        <button key={l} onClick={() => setLang(l)}
          style={{
            background: lang === l ? 'var(--bg-3)' : 'transparent',
            color: lang === l ? 'var(--fg-0)' : 'var(--fg-3)',
            border: 0, borderRadius: 100,
            padding: '3px 9px', cursor: 'pointer',
            letterSpacing: '0.06em',
            fontWeight: lang === l ? 600 : 400,
          }}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
